import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import type {
  ItemAssignmentResponse,
  ItemAssignmentSimpleResponse} from 'src/api/ProactApiClient';
import {
  ItemAssignmentStateEnum,
} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';

export const pageTitleDict = {
  singular: 'Item Assignment',
  plural: 'Item Assignments',
};

export const ItemAssignmentMeta = (
  personnel?: ISelectOption[]
): IFormFieldV2[] => [
  {
    name: 'assignedToUserId',
    label: 'Assigned To User',
    type: 'autocomplete',
    required: true,
    options: personnel || [],
  },
  {
    name: 'serial',
    label: 'Serial / Identifier',
    type: 'text',
  },
  {
    name: 'description',
    label: 'Description',
    type: 'textarea',
    validation: {
      maxLength: 1000,
    },
  },
  {
    name: 'createdAt',
    label: 'Created On',
    type: 'date',
    readOnly: true,
  },
  {
    name: 'assignedAt',
    label: 'Assigned On',
    type: 'date',
    readOnly: true,
  },
  {
    name: 'expiresAt',
    label: 'Expires On',
    type: 'date',
    readOnly: true,
  },
  {
    name: 'requestNote',
    label: 'Requestor Comment',
    type: 'textarea',
    validation: {
      maxLength: 1000,
    },
  },
];

export const CreateDefaultItemAssignment =
  (): Partial<ItemAssignmentResponse> => {
    return {
      assignedToUserId: undefined,
      serial: '',
      description: '',
      requestNote: '',
      state: ItemAssignmentStateEnum.Draft,
    };
  };

export const ItemAssignmentPillConfig = (
  obj?: ItemAssignmentResponse | ItemAssignmentSimpleResponse
): PillProps[] => {
  const pills: PillProps[] = [];

  switch (obj?.state) {
    case ItemAssignmentStateEnum.Draft:
      pills.push({
        text: 'Draft',
        color: 'yellow',
      });
      break;
    case ItemAssignmentStateEnum.PendingSupervisorApproval:
      pills.push({
        text: 'Pending Supervisor Approval',
        color: 'yellow',
      });
      break;
    case ItemAssignmentStateEnum.PendingAdminApproval:
      pills.push({
        text: 'Pending Admin Approval',
        color: 'yellow',
      });
      break;
    case ItemAssignmentStateEnum.PendingAssignment:
      pills.push({
        text: 'Pending Assignment',
        color: 'yellow',
      });
      break;
    case ItemAssignmentStateEnum.Assigned:
      pills.push({
        text: 'Assigned',
        color: 'blue',
      });
      break;
    case ItemAssignmentStateEnum.Expired:
      pills.push({
        text: 'Expired',
        color: 'red',
      });
      break;
    case ItemAssignmentStateEnum.ClosedWithReturn:
      pills.push({
        text: 'Closed',
        color: 'green',
      });
      break;
    case ItemAssignmentStateEnum.ClosedWithoutReturn:
      pills.push({
        text: 'Closed (Missing Item)',
        color: 'yellow',
      });
      break;
    case ItemAssignmentStateEnum.Rejected:
      pills.push({
        text: 'Rejected',
        color: 'red',
      });
      break;
    default:
      pills.push({
        text: 'Invalid',
        color: 'red',
      });
      break;
  }

  return pills;
};

export const itemAssignmentDocumentTitle = 'Item Assignment';

export function HandleItemAssignmentRowSubTextRender(
  item: ItemAssignmentSimpleResponse
): string {
  let text = item.internalIdentifier ?? 'Unknown Identifier';

  if (item.assignedAt) {
    text += ` - Issued On: ${new Date(item.assignedAt)
      .toString()
      .toShortDate()}`;
  }

  return text;
}

export function HandleItemAssignmentRowDetailRender(
  item: ItemAssignmentSimpleResponse
): React.ReactNode {
  return (
    <Box>
      <Group justify="left">
        <Box w={100}>
          <Text size="sm" mb="sm">
            Serial
          </Text>
          <Text c="dimmed" size="sm" mb="sm">
            {item.serial ? item.serial : 'N/A'}
          </Text>
        </Box>
        <Box>
          <Text size="sm" mb="sm">
            Description
          </Text>
          <Text c="dimmed" size="sm" mb="sm">
            {item.description ? item.description?.abbreviate(250) : 'N/A'}
          </Text>
        </Box>
      </Group>
    </Box>
  );
}
